"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { CommandSearch } from "@/components/features/search/CommandSearch";
import { Button } from "@/components/ui/button";
import { motion } from "motion/react";
import LoadingSpinner from '@/components/app/loading/LoadingSpinner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home,
  Search,
  Menu,
  User,
  Star,
  Building,
  FileText,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  ChevronLeft
} from "lucide-react";
import NavbarAuth from "./NavbarAuth";
import useCountryDetection, { availableCountries } from '@/components/patterns/hooks/use-country-detection';
import { useMobileNav } from '@/components/patterns/hooks/use-mobile-nav';
import { logoutAndRedirect } from '@/lib/auth-utils';
import Image from 'next/image';
import { cn, debounce } from '@/lib/utils';
import type { AuthState } from '@/lib/types/auth';
import { useClientMotion } from '@/lib/hooks/use-reduced-motion-ssr';

interface NavbarProps {
  initialAuth: AuthState;
}

export default function Navbar({ initialAuth }: NavbarProps) {
  const { MotionDiv, MotionHeader, motionProps } = useClientMotion();
  const router = useRouter();
  const pathname = usePathname();
  
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Pass auth state to mobile nav hook
  const { showMobileNav } = useMobileNav(initialAuth);
  
  const { loading: countryLoading, country: selectedCountry, selectCountry } = useCountryDetection();

  const navItemVariants = {
    hidden: { opacity: 0, y: -5 },
    visible: { opacity: 1, y: 0 }
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Reset loading state after navigation attempts (fallback)
  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 3000); // 3 second fallback timeout
      
      return () => clearTimeout(timer);
    }
  }, [isNavigating]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      setIsScrolled(window.scrollY > 20);
    }, 16); // ~60fps throttling
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCountrySelect = (country) => {
    selectCountry(country);
  };

  const handleSignOut = async () => {
    try {
      await logoutAndRedirect();
    } catch (error) {
      console.error("Error signing out:", error);
      window.location.href = "/";
    }
  };

  // Handle navigation with loading state
  const handleNavigation = (href: string) => {
    setIsNavigating(true);
    router.push(href);
  };

  return (
    <>
      {/* Global loading overlay */}
      {isNavigating && <LoadingSpinner />}
      
      <MotionHeader 
        {...motionProps({
          initial: { y: -100, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          transition: { duration: 0.5 }
        })}
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-md shadow-sm' 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 h-20">
          <div className="h-full flex items-center justify-between">
            {/* Logo Section */}
            <MotionDiv 
              {...motionProps({
                initial: "hidden",
                animate: "visible",
                variants: navItemVariants
              })}
              className="flex items-center"
            >
              <button 
                onClick={() => handleNavigation("/")} 
                disabled={isNavigating}
                className={`flex items-center group ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <Image 
                  src="/images/logo-wordmark.png" 
                  alt="RentWise Wordmark Logo" 
                  className="h-8 w-auto transition-transform transform-gpu group-hover:scale-105" 
                  width={120}
                  height={32}
                  priority
                />
              </button>
            </MotionDiv>

            {/* Navigation - Desktop only */}
            <nav className="hidden md:flex items-center space-x-8 mx-4">
              {/* For Landlords/Tenants Dropdown - Now Products */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants,
                  transition: { delay: 0.1 }
                })}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors px-1 -mb-0.5"
                    >
                      <span>Products</span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="center" 
                    className="w-48 p-2 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100"
                  >
                    <DropdownMenuItem 
                      className="flex items-center gap-2 py-2 hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-brand-primary)] rounded-lg cursor-pointer"
                      onClick={() => handleNavigation("/for-landlords")}
                    >
                        <Building className="h-4 w-4" />
                        <span>For Landlords</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 py-2 hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-brand-primary)] rounded-lg cursor-pointer"
                      onClick={() => handleNavigation("/for-tenants")}
                    >
                        <User className="h-4 w-4" />
                        <span>For Tenants</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </MotionDiv>

              {[
                { href: "/pricing", label: "Pricing" },
              ].map((item, index) => (
                <MotionDiv
                  key={item.href}
                  {...motionProps({
                    initial: "hidden",
                    animate: "visible",
                    variants: navItemVariants,
                    transition: { delay: 0.2 }
                  })}
                >
                  <button 
                    onClick={() => handleNavigation(item.href)}
                    className="text-sm font-medium text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors relative group"
                  >
                    {item.label}
                    <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-[var(--color-brand-primary)] scale-x-0 group-hover:scale-x-100 transition-transform transform-gpu origin-left" />
                  </button>
                </MotionDiv>
              ))}

              {[
                { href: "/listings", label: "Listings" },
              ].map((item, index) => (
                <MotionDiv
                  key={item.href}
                  {...motionProps({
                    initial: "hidden",
                    animate: "visible",
                    variants: navItemVariants,
                    transition: { delay: 0.3 }
                  })}
                >
                  <button 
                    onClick={() => handleNavigation(item.href)}
                    className="text-sm font-medium text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors relative group"
                  >
                    {item.label}
                    <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-[var(--color-brand-primary)] scale-x-0 group-hover:scale-x-100 transition-transform transform-gpu origin-left" />
                  </button>
                </MotionDiv>
              ))}
              
              {/* Reviews Dropdown */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants,
                  transition: { delay: 0.4 }
                })}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors px-1 -mb-0.5"
                    >
                      <span>Reviews</span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="center" 
                    className="w-48 p-2 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100"
                  >
                    <DropdownMenuItem 
                      className="flex items-center gap-2 py-2 hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-brand-primary)] rounded-lg cursor-pointer"
                      onClick={() => handleNavigation("/browse")}
                    >
                        <Star className="h-4 w-4" />
                        <span>Browse Reviews</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 py-2 hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-brand-primary)] rounded-lg cursor-pointer"
                      onClick={() => handleNavigation("/leave-review")}
                    >
                        <FileText className="h-4 w-4" />
                        <span>Leave a Review</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </MotionDiv>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Search Button - Desktop only */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants
                })}
                className="hidden sm:block"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsSearchOpen(true)}
                  className="text-gray-600 hover:text-[var(--color-brand-primary)] transition-colors"
                  aria-label="Search"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </MotionDiv>

              {/* Country Selector */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants
                })}
                className="z-50"
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-gray-700 hover:text-[var(--color-brand-primary)] hover:bg-[var(--color-hover-overlay)] transition-all px-2 sm:px-3 rounded-full" 
                      size="sm"
                      aria-label="Select country"
                    >
                      <span className="text-lg">{isClient ? (selectedCountry?.flag || '🌐') : '🌐'}</span>
                      <span className="text-sm font-medium hidden sm:inline">{isClient ? (selectedCountry?.name || 'Global') : 'Global'}</span>
                      <ChevronDown className="h-4 w-4 opacity-50 hidden sm:inline" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-56 p-2 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-100 z-50"
                    sideOffset={8}
                    forceMount
                  >
                    {availableCountries.map((country) => (
                      <DropdownMenuItem
                        key={country.name}
                        className={`
                          flex items-center justify-between py-2.5 px-4 rounded-lg cursor-pointer
                          transition-all duration-200
                          ${country.available 
                            ? 'hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-brand-primary)] active:scale-98' 
                            : 'opacity-60 cursor-not-allowed'
                          }
                          ${selectedCountry?.name === country.name 
                            ? 'bg-[var(--color-hover-overlay)] text-[var(--color-brand-primary)]' 
                            : ''
                          }
                        `}
                        onClick={() => handleCountrySelect(country)}
                        disabled={!country.available}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{country.flag}</span>
                          <span className="font-medium">{country.name}</span>
                        </div>
                        {!country.available ? (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            Coming Soon
                          </span>
                        ) : selectedCountry?.name === country.name && (
                          <div className="h-2 w-2 rounded-full bg-[var(--color-brand-primary)]" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </MotionDiv>

              {/* Sign In or Dashboard Button - Desktop only */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants
                })}
                className="hidden md:block"
              >
                <NavbarAuth initialAuth={initialAuth} />
              </MotionDiv>

              {/* Small Screen Auth Button */}
              <MotionDiv
                {...motionProps({
                  initial: "hidden",
                  animate: "visible",
                  variants: navItemVariants
                })}
                className="md:hidden"
              >
                <NavbarAuth initialAuth={initialAuth} />
              </MotionDiv>
            </div>
          </div>
        </div>
      </MotionHeader>

      {/* Mobile Bottom Navigation Bar - Only show on specific routes */}
      {showMobileNav && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-lg">
        <div className="flex items-center justify-between px-8 py-3">
          <button 
            onClick={() => handleNavigation("/listings")}
            disabled={isNavigating}
            className={`flex flex-col items-center justify-center w-16 text-gray-500 hover:text-[var(--color-brand-primary)] transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
            aria-label="Listings"
          >
            <Search className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{isNavigating ? "Loading..." : "Listings"}</span>
          </button>
          
          <button
            onClick={() => handleNavigation("/")}
            disabled={isNavigating}
            className={`flex flex-col items-center justify-center w-16 ${
              pathname === "/" 
              ? 'text-[var(--color-brand-primary)]' 
              : 'text-gray-500 hover:text-[var(--color-brand-primary)]'
            } transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <Home className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{isNavigating ? "Loading..." : "Home"}</span>
          </button>
          
          <Sheet>
            <SheetTrigger asChild>
              <button 
                className="flex flex-col items-center justify-center w-16 text-gray-500 hover:text-[var(--color-brand-primary)] transition-colors"
                aria-label="Menu"
              >
                <Menu className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium">Menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full p-0 border-none">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col h-full">
                {/* Header with close button */}
                <div className="flex items-center justify-between p-4">
                  <button 
                    onClick={() => handleNavigation("/")} 
                    disabled={isNavigating}
                    className={`flex items-center group ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    <Image 
                      src="/images/logo-wordmark.png" 
                      alt="RentWise Wordmark Logo" 
                      className="h-8 w-auto transition-transform transform-gpu group-hover:scale-105" 
                      width={120}
                      height={32}
                      priority
                    />
                  </button>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8">
                      <ChevronLeft className="h-5 w-5" />
                      <span className="sr-only">Close menu</span>
                    </Button>
                  </SheetTrigger>
                </div>
                
                {/* Main Navigation Links - Reorganized and Styled */}
                <div className="flex-1 overflow-y-auto px-4">
                  {/* Primary Navigation - Styled as cards */}
                  <div className="grid grid-cols-2 gap-3 mb-6 mt-6">
                    <button
                      onClick={() => handleNavigation("/")}
                      disabled={isNavigating}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl bg-[var(--color-hover-overlay)] border border-[var(--color-neutral-300)] hover:bg-[var(--color-neutral-100)] transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <Home className="h-7 w-7 text-[var(--color-brand-primary)] mb-2" />
                      <span className="font-medium text-sm text-[var(--color-brand-primary)]">{isNavigating ? "Loading..." : "Home"}</span>
                    </button>
                    
                    <button
                      onClick={() => handleNavigation("/listings")}
                      disabled={isNavigating}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl bg-[var(--color-rent-secondary)]/20 border border-[var(--color-rent-secondary)] hover:bg-[var(--color-rent-secondary)]/30 transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <Search className="h-7 w-7 text-[var(--color-rent-primary)] mb-2" />
                      <span className="font-medium text-sm text-[var(--color-rent-primary)]">{isNavigating ? "Loading..." : "Listings"}</span>
                    </button>
                    
                    <button
                      onClick={() => handleNavigation("/browse")}
                      disabled={isNavigating}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl bg-[var(--color-footer-accent)]/20 border border-[var(--color-footer-accent)] hover:bg-[var(--color-footer-accent)]/30 transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <Star className="h-7 w-7 text-[var(--color-footer-accent)] mb-2" />
                      <span className="font-medium text-sm text-[var(--color-footer-accent)]">{isNavigating ? "Loading..." : "Browse Reviews"}</span>
                    </button>
                    
                    <button
                      onClick={() => handleNavigation("/leave-review")}
                      disabled={isNavigating}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl bg-[var(--color-cta-accent)]/20 border border-[var(--color-cta-accent)] hover:bg-[var(--color-cta-accent)]/30 transition-colors ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <FileText className="h-7 w-7 text-[var(--color-cta-accent)] mb-2" />
                      <span className="font-medium text-sm text-[var(--color-cta-accent)]">{isNavigating ? "Loading..." : "Leave Review"}</span>
                    </button>
                  </div>
                  
                  {/* Secondary Navigation - List style */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 px-1">For Users</h3>
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
                      <button
                        onClick={() => handleNavigation("/for-tenants")}
                        disabled={isNavigating}
                        className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 text-gray-700 hover:bg-gray-50 hover:text-[var(--color-brand-primary)] transition-colors w-full text-left ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <User className="h-5 w-5 text-gray-500" />
                        <span className="font-medium">{isNavigating ? "Loading..." : "For Tenants"}</span>
                      </button>
                      
                      <button
                        onClick={() => handleNavigation("/for-landlords")}
                        disabled={isNavigating}
                        className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[var(--color-brand-primary)] transition-colors w-full text-left ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <Building className="h-5 w-5 text-gray-500" />
                        <span className="font-medium">{isNavigating ? "Loading..." : "For Landlords"}</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Information Links */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 px-1">Information</h3>
                    <div className="flex items-center px-1">
                      <button
                        onClick={() => handleNavigation("/pricing")}
                        disabled={isNavigating}
                        className={`flex-1 text-left text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors py-2 px-2 rounded-md hover:bg-gray-100 ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <span className="font-medium">{isNavigating ? "Loading..." : "Pricing"}</span>
                      </button>
                      
                      <button
                        onClick={() => handleNavigation("/faq")}
                        disabled={isNavigating}
                        className={`flex-1 text-center text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors py-2 px-2 rounded-md hover:bg-gray-100 ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <span className="font-medium">{isNavigating ? "Loading..." : "FAQ"}</span>
                      </button>
                      
                      <button
                        onClick={() => handleNavigation("/about")}
                        disabled={isNavigating}
                        className={`flex-1 text-right text-gray-700 hover:text-[var(--color-brand-primary)] transition-colors py-2 px-2 rounded-md hover:bg-gray-100 ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <span className="font-medium">{isNavigating ? "Loading..." : "About"}</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="mx-4 mb-6">
                    <NavbarAuth initialAuth={initialAuth} inMobileMenu={true} />
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      )}

      {/* Command Search Dialog */}
      <CommandSearch isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
} 